import React, { useCallback, useContext } from 'react';
import styles from './webDealLine.module.scss';
import { IconWrapper } from '../../../common/IconWrapper';
import {
    faCheckCircle,
    faMinusCircle,
    faPlusCircle,
    faTimesCircle,
    faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import { useAlert } from 'react-alert';
import AuthContext from '../../../authentication/AuthContext';
import axios from 'axios';
import UserContext from '../../../user/UserContext';
import _ from 'lodash';
import ProductInformation from '../../../common/product-information/ProductInformation';

export default function WebDealLine({
    dealLine: {
        id,
        image,
        productCode,
        productDescription,
        unitSize,
        wasSellPrice,
        sellPrice,
        wasRSP,
        rsp,
        min,
        max,
        multiple,
        remainingStock,
        acceptedQuantity,
        currentQuantity,
        submitStatus,
        warningMessage,
        margin,
        wasMargin,
        bestBefore
    },
    getWebDealLines
}) {
    const alert = useAlert();
    const cas = useContext(AuthContext);
    const { userDetails } = useContext(UserContext);
    const quantity = currentQuantity ? currentQuantity : 0;

    const decrementQuantity = () => {
        axios
            .post(
                window.CONFIG['ws.activoc-web-deals-public.deal.line.decrement']
                    .replace('{franchiseCode}', userDetails.currentFranchise.code)
                    .replace('{lineId}', id),
                {},
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .then(getWebDealLines)
            .catch(() => alert.error('ERROR: Unable to decrement quantity'));
    };
    const incrementQuantity = () => {
        axios
            .post(
                window.CONFIG['ws.activoc-web-deals-public.deal.line.increment']
                    .replace('{franchiseCode}', userDetails.currentFranchise.code)
                    .replace('{lineId}', id),
                {},
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .then(getWebDealLines)
            .catch(() => alert.error('ERROR: Unable to increment quantity'));
    };

    const throttledIncrement = useCallback(
        _.throttle(() => incrementQuantity(), 1000),
        []
    );

    const getStatusIcon = () => {
        switch (submitStatus) {
            case 'SUCCESS':
                return (
                    <IconWrapper
                        icon={faCheckCircle}
                        className={'icon-success'}
                        data-testid={'success-icon'}
                    />
                );
            case 'FAIL':
                return (
                    <IconWrapper icon={faTimesCircle} className={'icon-error'} data-testid={'fail-icon'} />
                );
            default:
                return '-';
        }
    };

    const numberToPrice = numberField => {
        return numberField ? '£' + numberField.toFixed(2) : 'unset';
    };

    const numberToPercentage = numberField => {
        return numberField ? numberField.toFixed(2) + '%' : 'unset';
    };

    return (
        <>
            <tr className={`no-pointer ${styles.webDealLine}`}>
                <td>
                    <ProductInformation
                        url={window.CONFIG['ws.activoc-web-deals-public.lines.product.info.full']
                            .replace('{franchiseCode}', userDetails.currentFranchise.code)
                            .replace('{webDealLineId}', id)}
                        image={image}
                        description={productDescription}
                    />
                </td>
                <td>{productCode}</td>
                <td>
                    {productDescription}
                    <br />
                    {bestBefore && (
                        <span className={styles.bestBeforeInfo}>
                            <IconWrapper icon={faExclamationCircle} className={styles.bestBeforeIcon} />
                            BBE: {bestBefore}
                        </span>
                    )}
                </td>
                <td>{unitSize}</td>
                <td className={styles.doubles}>
                    {numberToPrice(sellPrice)} <br /> <span>{numberToPrice(wasSellPrice)}</span>
                </td>
                <td className={styles.doubles}>
                    {numberToPrice(rsp)} <br /> <span>{numberToPrice(wasRSP)}</span>
                </td>
                <td className={styles.doubles}>
                    {numberToPercentage(margin)} <br /> <span>{numberToPercentage(wasMargin)}</span>
                </td>
                <td>{min === 0 ? '-' : min}</td>
                <td>{max === 0 ? '-' : max}</td>
                <td>{multiple}</td>
                <td>{remainingStock}</td>
                <td>{acceptedQuantity}</td>
                <td>
                    <div className={`flex ${styles.quantitySelector}`}>
                        <Button
                            onClick={decrementQuantity}
                            tabIndex={-1}
                            disabled={quantity <= 0}
                            className={styles.quantityButton}
                            data-testid={'decrement'}
                        >
                            <IconWrapper className={'qty-decrease'} icon={faMinusCircle} />
                        </Button>
                        <span className={styles.quantity}>{quantity}</span>
                        <Button
                            onClick={() => {
                                if (quantity === 0) {
                                    throttledIncrement();
                                } else {
                                    incrementQuantity();
                                }
                            }}
                            tabIndex={-1}
                            disabled={
                                (max && quantity >= max) || (remainingStock && quantity >= remainingStock)
                            }
                            className={styles.quantityButton}
                            data-testid={'increment'}
                        >
                            <IconWrapper icon={faPlusCircle} />
                        </Button>
                    </div>
                </td>
                <td>{getStatusIcon()}</td>
            </tr>
            {warningMessage && (
                <tr className={styles.webDealErrorLine}>
                    <td colSpan={'100%'} className={styles.warningText}>
                        {warningMessage}
                    </td>
                </tr>
            )}
        </>
    );
}
